/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: CustomApplicationsPage.tsx
 🗓️ Created: 2026-01-16
 🧩 Description:
    Custom Applications list page - follows Extensions module pattern.
    Uses useCustomApplicationsList hook for DataGrid management.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContextStore } from '../../store/context';
import { Code, Plus, Edit, Trash2 } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useNotifications } from '../../contexts/NotificationContext';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { deleteCustomApplication } from './api/customApplicationsApi';
import { useCustomApplicationsList } from './hooks/useCustomApplicationsList';
import type { CustomApplication, CustomApplicationColumn } from './types';

// Status badge colors
const APP_TYPE_COLORS: Record<string, string> = {
    lua: 'bg-blue-500/20 text-blue-600 dark:text-blue-400',
    xml: 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
};

export default function CustomApplicationsPage() {
    const navigate = useNavigate();
    const { t, i18n } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const { selectedDomain } = useContextStore();

    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; application: CustomApplication | null }>({
        isOpen: false,
        application: null,
    });

    // Define columns
    const availableColumns = useMemo<CustomApplicationColumn[]>(() => [
        {
            key: 'name',
            label: t('customApplications.table.name'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: CustomApplication) => (
                <span className="font-medium text-primary">{item?.name || '-'}</span>
            ),
        },

        {
            key: 'appType',
            label: t('customApplications.table.appType'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: CustomApplication) => (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${APP_TYPE_COLORS[item?.appType] || ''}`}>
                    {item?.appType?.toUpperCase() || '-'}
                </span>
            ),
        },
        {
            key: 'finalDestination',
            label: t('customApplications.table.finalDestination'),
            sortable: false,
            visible: true,
            render: (_value: unknown, item: CustomApplication) => (
                <div className="text-sm text-muted-foreground">
                    {item?.skipFinalDestination ? (
                        <span className="italic">{t('customApplications.form.skipFinalDestination')}</span>
                    ) : item?.finalDestinationModule ? (
                        <>
                            {t(`customApplications.destinationModules.${item.finalDestinationModule}`)}: {item.finalDestinationValue}
                        </>
                    ) : (
                        '-'
                    )}
                </div>
            ),
        },
        {
            key: 'enabled',
            label: t('customApplications.table.enabled'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: CustomApplication) => (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                    {item?.enabled ? t('common.enabled') : t('common.disabled')}
                </span>
            ),
        },
        {
            key: 'actions',
            label: t('common.actions'),
            sortable: false,
            visible: true,
        },
    ], [t, i18n.language]);

    // Use custom hook for list management
    const {
        data,
        loading,
        columns,
        pagination,
        searchQuery,
        sortBy,
        sortOrder,
        handleSearch,
        handleSort,
        handlePageChange,
        handleItemsPerPageChange,
        handleColumnToggle,
        refresh,
    } = useCustomApplicationsList({
        initialColumns: availableColumns,
        initialSortBy: 'name',
        initialSortOrder: 'asc',
        initialLimit: 10,
        domainId: selectedDomain?.id,
    });

    // Handlers
    const handleCreate = useCallback(() => {
        navigate('/pbx/applications/custom-applications/new');
    }, [navigate]);

    const handleEdit = useCallback((application: CustomApplication) => {
        navigate(`/pbx/applications/custom-applications/${application.uuid || application.id}`);
    }, [navigate]);

    const handleDelete = useCallback((application: CustomApplication) => {
        setDeleteModal({ isOpen: true, application });
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!deleteModal.application) return;

        try {
            await deleteCustomApplication(String(deleteModal.application.uuid || deleteModal.application.id));
            showSuccess(t('customApplications.messages.deleteSuccess'));
            setDeleteModal({ isOpen: false, application: null });
            refresh();
        } catch (error) {
            console.error('Error deleting application:', error);
            showError(error.message || t('customApplications.messages.deleteError'));
        }
    }, [deleteModal.application, refresh, showError, showSuccess, t]);

    // Render actions for each row
    const renderActions = useCallback((application: CustomApplication) => (
        <div className="flex items-center gap-2">
            <SimpleTooltip content={t('common.edit')} position="top">
                <button
                    onClick={() => handleEdit(application)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={t('common.delete')} position="top">
                <button
                    onClick={() => handleDelete(application)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                </button>
            </SimpleTooltip>
        </div>
    ), [t, handleEdit, handleDelete]);

    return (
        <div className="p-6">
            <DataGridSimple
                data={data}
                loading={loading}
                columns={columns}
                pagination={pagination}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSearch={handleSearch}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                onColumnToggle={handleColumnToggle}
                renderActions={renderActions}
                searchPlaceholder={t('customApplications.searchPlaceholder')}
                title={t('customApplications.title')}
                titleIcon={<Code className="h-5 w-5 text-primary" />}
                actions={
                    <div className="flex items-center gap-2">
                        <SimpleTooltip content={t('customApplications.actions.add')} position="top">
                            <button
                                onClick={handleCreate}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
                                <Plus className="h-4 w-4" /> {t('customApplications.actions.add')}
                            </button>
                        </SimpleTooltip>
                    </div>
                }
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, application: null })}
                onConfirm={confirmDelete}
                title={t('customApplications.actions.delete')}
                message={`${t('customApplications.messages.deleteConfirm')} (${deleteModal.application?.name})`}
                confirmText={t('customApplications.actions.delete')}
                cancelText={t('common.cancel')}
                type="danger"
            />
        </div>
    );
}
